import { CupIcon } from './icons';
import { useT } from '../i18n';

interface RewardProgress {
  threshold: number;
  available: boolean;
}

interface Props {
  stamps: number;
  rewards: RewardProgress[];
  /**
   * Bypasses the visitor's chosen language — for the (Spanish-only) admin
   * panel, which pins its own `lang`/`dir` but reuses this component. Without
   * this, a staff member who last browsed the public site in another
   * language would see this one card switch languages mid-panel, since it
   * reads the same shared i18n context as the storefront.
   */
  locked?: { cardTitle: string; stampsLabel: (n: number) => string };
}

/**
 * The "cartilla": a grid of circles, each holding a cup glyph. Filled slots
 * invert to brown-on-cream so progress reads at a glance.
 *
 * There is no fixed card size any more — rewards are redeemed by spending
 * their own cost, not by filling a set number of slots. The grid instead
 * scales to whichever configured reward is soonest out of reach, so it still
 * reads as "progress toward something." Once every reward is already
 * affordable (or none are configured), it just shows the raw count.
 */
export function StampCard({ stamps, rewards, locked }: Props) {
  const t = useT();
  const cardTitle = locked?.cardTitle ?? t.card.title;
  const stampsLabel = locked?.stampsLabel ?? t.card.stamps;
  const upcoming = rewards
    .filter((r) => !r.available)
    .sort((a, b) => a.threshold - b.threshold)[0];
  const scale = Math.max(upcoming?.threshold ?? stamps, 1);
  const slots = Array.from({ length: scale }, (_, i) => i < stamps);

  return (
    <div className="card">
      <div className="card-head">
        <div className="eyebrow eyebrow--tight">{cardTitle}</div>
        <div className="count">{stampsLabel(stamps)}</div>
      </div>
      <div className="stamp-grid" role="img" aria-label={stampsLabel(stamps)}>
        {slots.map((filled, i) => (
          <div key={i} className={filled ? 'stamp stamp--filled' : 'stamp'}>
            <CupIcon color={filled ? '#F2EDE3' : '#D9CFBE'} />
          </div>
        ))}
      </div>
    </div>
  );
}
